import { useIsMounted } from "client/Hooks";
import { setTrackingId } from "client/Misc/utils";
import {
	PropsWithChildren,
	ReactElement,
	RefObject,
	TransitionEvent,
	useLayoutEffect,
	useRef
} from "react";
import { TreeAccordionActionList } from "./TreeAccordionActionList";
import { TreeAccordionItem } from "./TreeAccordionItem";
import treeAccordionItemStyles from "./TreeAccordionItem.module.less";
import styles from "./TreeAccordion.module.less";
import { reflow } from "client/Misc/domUtils";

export type TreeAccordionProps = {
	trackingId?: string;
};

export const TreeAccordion = (props: PropsWithChildren<TreeAccordionProps>): ReactElement => {
	const {
		trackingId,
		children
	} = props;

	return (
		<ul
			{...setTrackingId(trackingId)}
			className={styles.root}
		>
			{ children }
		</ul>
	);
};
TreeAccordion.displayName = "TreeAccordion";

const TreeAccordionNode = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<li className={styles.node}>
			{ children }
		</li>
	);
};
TreeAccordionNode.displayName = "TreeAccordion.Node";

export type TreeAccordionSubTreeProps = {
	isCollapsed: boolean;
	trackingId?: string;
};

const TreeAccordionSubTree = (props: PropsWithChildren<TreeAccordionSubTreeProps>): ReactElement | null => {
	const {
		trackingId,
		isCollapsed,
		children
	} = props;
	const ref: RefObject<HTMLUListElement | null> = useRef(null);

	const isMounted: () => boolean = useIsMounted();

	useLayoutEffect(
		() => {
			const element: HTMLUListElement | null = ref.current;
			if (!element) {
				return;
			}

			if (!isMounted()) {
				if (isCollapsed) {
					element.style.display = "none";
				}
				return;
			}

			if (!isCollapsed) {
				element.style.display = "";
			}
			element.style.height = "";

			const elementHeight: number = element.offsetHeight;
			const from: number = isCollapsed ? elementHeight : 0;
			const to: number = isCollapsed ? 0 : elementHeight;

			if (from === to) {
				if (isCollapsed) {
					element.style.display = "none";
				}
				return;
			}

			element.style.height = `${from}px`;

			// force Firefox to reflow
			// otherwise sometimes animation do not start
			reflow(element);

			setTimeout(
				() => {
					element.style.height = `${to}px`;
				},
				0
			);
		},
		[isCollapsed, isMounted]
	);

	const transitionEndHandler = (args: TransitionEvent<HTMLUListElement>): void => {
		const element: HTMLUListElement | null = ref.current;
		if (!element || args.target !== element) {
			return;
		}

		if (isCollapsed) {
			element.style.display = "none";
		}
		element.style.height = "";
	};

	return (
		<ul
			{...setTrackingId(trackingId)}
			className={styles.subTree}
			onTransitionEnd={transitionEndHandler}
			ref={ref}
		>
			{ children }
		</ul>
	);
};
TreeAccordionSubTree.displayName = "TreeAccordion.SubTree";

const TreeAccordionSkeleton = (): ReactElement => {
	return (
		<div className={treeAccordionItemStyles.skeleton} />
	);
};
TreeAccordionSkeleton.displayName = "TreeAccordion.Skeleton ";

export type TreeAccordionSeparatorProps = {
	indentionLevel?: number;
};

const TreeAccordionSeparator = (props: TreeAccordionSeparatorProps): ReactElement => {
	const {
		indentionLevel = 0
	} = props;

	return (
		<div className={styles.separatorHost}>
			<hr
				className={styles.separator}
				style={{ ["--_indention-level"]: indentionLevel }}
			/>
		</div>
	);
};
TreeAccordionSeparator.displayName = "TreeAccordionSeparator";

TreeAccordion.Node = TreeAccordionNode;
TreeAccordion.Item = TreeAccordionItem;
TreeAccordion.SubTree = TreeAccordionSubTree;
TreeAccordion.ActionList = TreeAccordionActionList;
TreeAccordion.Skeleton = TreeAccordionSkeleton;
TreeAccordion.Separator = TreeAccordionSeparator;
